/**
 * ParticipantPicker — a reusable SkyDropdown typeahead over GET /api/v1/participants for the standalone
 * member-keyed screens (vitals, encounters, medications, utilization, etc.). It emits the surrogate
 * enterprise_member_key (a non-PHI bigint string); the display label is "Last, First" (the same
 * minimum-necessary identity the Roster already renders) - never MRN/medicare_id/participant_id.
 * Rows are never logged.
 *
 * P1-1: the picker is a DEBOUNCED SERVER-SIDE typeahead. Typing drives the SAME ?q= search the Roster
 * uses (op_dim_member first_name/last_name ILIKE), so ANY of the ~2299 members is reachable - not only
 * the first page. On an empty query it loads the first page and shows a "showing first N of M - search to
 * find others" affordance (hyphen, never an em-dash). The currently-selected member is kept pinned in the
 * option list so its label survives a later search and a deep-link (?member=) never blanks the trigger.
 */
import { useEffect, useRef, useState } from "react";
import { SkyDropdown, SkyText, type DropdownOption } from "@skypointcloud/skypoint-ui";
import type { Participant } from "@shared/contracts";
import { fetchEnvelope } from "../lib/api";

const PAGE_SIZE = 50;
const DEBOUNCE_MS = 250;

function toOption(p: Participant): DropdownOption {
  return { value: p.enterprise_member_key, text: `${p.last_name}, ${p.first_name}` };
}

export function ParticipantPicker({
  value,
  onSelect,
  label = "Participant",
}: {
  value?: string;
  onSelect: (enterpriseMemberKey: string) => void;
  label?: string;
}) {
  const [options, setOptions] = useState<DropdownOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  // A fetch failure must be DISTINGUISHABLE from "no matches": without this, an empty list on a failed
  // search reads as "no such participant" when the search never actually ran. The debounced re-fetch is
  // the recovery — typing again re-issues the query — so this is a hint, not a dead end.
  const [fetchError, setFetchError] = useState(false);
  // The live search query, fed by the dropdown's own search box (onSearchChange) and debounced below.
  const [query, setQuery] = useState("");
  // The selected member's option, captured at selection time so it stays pinned in the list (and the
  // trigger label survives) even after a later search replaces the fetched options.
  const selectedRef = useRef<DropdownOption | null>(null);

  // Debounced server fetch: empty query -> first page; a query -> ?q= against the same roster endpoint.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(() => {
      const params = new URLSearchParams({ page: "1", pageSize: String(PAGE_SIZE) });
      const q = query.trim();
      if (q) params.set("q", q);
      fetchEnvelope<Participant[]>(`/api/v1/participants?${params.toString()}`)
        .then(({ data, meta }) => {
          if (cancelled) return;
          const opts = data.map(toOption);
          // Keep the selected option present so the trigger never blanks and the pick stays clearable.
          const sel = selectedRef.current;
          if (sel && !opts.some((o) => o.value === sel.value)) opts.unshift(sel);
          setOptions(opts);
          if (meta?.total != null) setTotal(meta.total);
          setFetchError(false);
        })
        .catch(() => {
          if (cancelled) return;
          setOptions(selectedRef.current ? [selectedRef.current] : []);
          // Flag the failure so the empty list reads as "search failed", not "no matches". Total stays as
          // its last value; the cap affordance is suppressed while errored (below).
          setFetchError(true);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query]);

  // On the unfiltered first page only, tell the user the list is capped and search reaches the rest.
  // Suppressed while errored (the list is not a trustworthy first page then).
  const showCapAffordance =
    !fetchError && query.trim() === "" && total > 0 && total > options.length;

  return (
    <div className="flex flex-col gap-1">
      <SkyDropdown
        label={label}
        options={options}
        loading={loading}
        placeholder="Search a participant by name"
        selectedOptions={value ? [value] : undefined}
        onSearchChange={setQuery}
        onOptionSelect={(values) => {
          const v = values[0];
          if (!v) return;
          const opt = options.find((o) => o.value === v);
          if (opt) selectedRef.current = opt;
          // A successful selection means the list the user picked from is trustworthy: clear any stale
          // fetch-error hint so it does not linger over a valid pick (selecting fires no re-fetch when the
          // query is unchanged, so the .then clear below would otherwise never run).
          setFetchError(false);
          onSelect(v);
        }}
      />
      {showCapAffordance && (
        <SkyText size={200} color="muted">
          Showing first {options.length} of {total} - search to find others
        </SkyText>
      )}
      {fetchError && (
        <SkyText size={200} color="danger">
          Could not load participants - type to search again
        </SkyText>
      )}
    </div>
  );
}
